import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, Search } from "lucide-react";
import ProductImage from "../components/ProductImage";
import { getActiveProducts } from "../services/storefrontProducts";
import { PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED } from "../config/featureFlags";

const CATEGORY_OPTIONS = [
  { label: "All Products", value: "all" },
  { label: "Flour", value: "flour" },
  { label: "Spices", value: "spices" },
  { label: "Pap", value: "pap" },
  { label: "Soup Ingredients", value: "soup ingredients" },
];
const BUSINESS_LINE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Pantry", value: "Pantry" },
  { label: "ProBioDelights", value: "ProBioDelights" },
].filter((option) => PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED || option.value !== "ProBioDelights");

const normalizeText = (value = "") => String(value || "").trim().toLowerCase();
const formatCurrency = (amount) =>
  `NGN ${Number(amount || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeBusinessLine, setActiveBusinessLine] = useState(() => {
    const requested = searchParams.get("business_line");
    return PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED && requested === "ProBioDelights" ? requested : "all";
  });

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const rows = await getActiveProducts();
        if (!cancelled) {
          setProducts(rows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load products.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const query = normalizeText(searchTerm);
    const category = normalizeText(activeCategory);
    const businessLine = normalizeText(activeBusinessLine);

    return products.filter((product) => {
      const productCategory = normalizeText(product.category);
      const productBusinessLine = normalizeText(product.business_line);
      const searchableText = normalizeText(
        `${product.name} ${product.slug} ${product.description} ${product.category}`
      );
      const matchesSearch = !query || searchableText.includes(query);
      const matchesCategory = category === "all" || productCategory === category;
      const matchesBusinessLine = businessLine === "all" || productBusinessLine === businessLine;
      return matchesSearch && matchesCategory && matchesBusinessLine;
    });
  }, [activeBusinessLine, activeCategory, products, searchTerm]);

  const selectBusinessLine = (value) => {
    setActiveBusinessLine(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value === "all") nextParams.delete("business_line");
    else nextParams.set("business_line", value);
    setSearchParams(nextParams);
  };

  return (
    <main className="min-h-screen bg-[#f7fbef] px-4 py-24 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 rounded-[2rem] border border-lime-200 bg-white/80 px-6 py-8 shadow-sm backdrop-blur">
          <p className="text-sm font-bold uppercase tracking-[0.28em] text-red-700">
            Zea Mays Products
          </p>
          <h1 className="mt-2 text-3xl font-bold text-slate-950 sm:text-4xl">
            Product Catalog
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Search and filter our live product catalog.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {BUSINESS_LINE_OPTIONS.map((option) => {
              const active = activeBusinessLine === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => selectBusinessLine(option.value)}
                  className={`rounded-full px-5 py-2.5 text-sm font-bold transition ${
                    active ? "bg-slate-950 text-white" : "border border-lime-200 bg-lime-50 text-slate-700 hover:border-red-200 hover:text-red-700"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <section className="mb-6 rounded-[1.75rem] border border-lime-200 bg-white/90 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.06)] backdrop-blur">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <label className="relative block">
              <span className="sr-only">Search products</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by name, slug, description, or category"
                className="h-12 w-full rounded-2xl border border-lime-200 bg-lime-50 pl-10 pr-4 text-sm outline-none transition focus:border-red-600 focus:bg-white focus:ring-2 focus:ring-red-100"
              />
            </label>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:justify-end">
              <Filter className="h-5 w-5 shrink-0 text-slate-500" />
              <div className="flex min-w-max flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((option) => {
                  const active = activeCategory === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setActiveCategory(option.value)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                        active
                          ? "bg-red-700 text-white"
                          : "border border-lime-200 bg-white text-slate-700 hover:border-red-200 hover:text-red-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="rounded-3xl bg-white p-10 text-center text-slate-700 shadow-sm">
            Loading products...
          </div>
        ) : error ? (
          <div className="rounded-3xl bg-red-50 p-10 text-center text-red-700 shadow-sm">
            {error}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="rounded-3xl bg-white p-10 text-center text-slate-700 shadow-sm">
            No products found.
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between text-sm text-slate-600">
              <span>
                {filteredProducts.length} product{filteredProducts.length === 1 ? "" : "s"} shown
              </span>
              <span className="font-semibold text-red-700">Live stock updates</span>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredProducts.map((product) => {
                const startingPrice = Math.min(
                  ...(product.options || [{ price: product.price || 0 }]).map((option) => option.price)
                );
                const isOutOfStock = Number(product.stock || 0) <= 0;

                return (
                  <Link
                    key={product.slug}
                    to={`/product/${product.slug}`}
                    className={`group flex h-full flex-col overflow-hidden rounded-[1.5rem] border bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg ${
                      isOutOfStock ? "border-red-200 opacity-80" : "border-lime-200"
                    }`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-lime-50">
                      <ProductImage
                        src={product.image}
                        alt={product.name}
                        className="h-full w-full transition duration-500 group-hover:scale-105"
                        width="400"
                        height="300"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-transparent" />
                      <span
                        className={`absolute right-3 top-3 rounded-full px-3 py-1 text-xs font-bold ${
                          isOutOfStock
                            ? "bg-red-600 text-white"
                            : "bg-emerald-600 text-white"
                        }`}
                      >
                        {isOutOfStock ? "Out of Stock" : "In Stock"}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <div className="flex items-start justify-between gap-3">
                        <h2 className="text-xl font-semibold text-slate-950">{product.name}</h2>
                        <div className="flex flex-col items-end gap-1">
                          {PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold text-white">
                            {product.business_line || "Pantry"}
                          </span> : null}
                          <span className="rounded-full bg-lime-50 px-3 py-1 text-xs font-semibold text-slate-600">
                            {product.category || "Uncategorized"}
                          </span>
                        </div>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {product.description || "No description available."}
                      </p>
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <span className="font-bold text-red-700">
                          From {formatCurrency(startingPrice)}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default Products;
